<template>
  <section></section>
</template>

<script>
import { Centrifuge } from 'centrifuge';
import { mapGetters } from 'vuex';
import debounce from 'lodash/debounce';
import {PROGRAM_DETAILS_REQUEST, PROGRAMS_LIST_REQUEST} from '@/store/actions/programs';
import {FETCH_NOTIFICATION_BADGES} from '@/store/actions/notifications';
import {FETCH_PLATFORM_ALERTS} from '@/store/actions/platformAlerts';
import {TICKET_LIST_REQUEST} from '@/store/actions/ticketList';

export default {
  name: 'InsuranceNotifications',
  created() {
    this.debouncedFetchBadges = debounce(() => {
      this.$store.dispatch(FETCH_NOTIFICATION_BADGES);
    }, 1000);
    // Debounced for the same reason as the badges: a multi-number WhatsApp outage publishes one
    // event PER number, and each would otherwise be its own request. Errors are swallowed — a
    // banner that cannot be refreshed must not become a toast.
    this.debouncedFetchPlatformAlerts = debounce(() => {
      this.$store.dispatch(FETCH_PLATFORM_ALERTS).catch(() => {});
    }, 1000);
  },
  watch: {
    insuranceCompanies: {
      handler() {
        this.subscribeToChannels();
      },
      deep: true,
    },
  },
  mounted() {
    this.subscribeToUserChannel();
  },
  computed: {
    ...mapGetters([
      'insuranceCompanies',
      'userSettings',
    ]),
  },
  methods: {
    truncate(text, max = 100) {
      if (text === undefined || text === null) return '';
      const s = String(text);
      return s.length > max ? `${s.slice(0, max)}...` : s;
    },
    subscribeToChannels() {
      const channels = this.insuranceCompanies.map((val) => val.insuranceChannel);
      this.initWebsocket(channels);
    },
    subscribeToUserChannel() {
      this.initWebsocket([this.userSettings.userChannel]);
    },
    initWebsocket(channels) {
      const centrifuge = new Centrifuge(window.WebsocketUrl, { token: localStorage.getItem('ins-token') });
      centrifuge.on('disconnected', (ctx) => {
        console.warn('[centrifuge] disconnected', ctx.code, ctx.reason);
      });
      centrifuge.on('error', (ctx) => {
        console.error('[centrifuge] error', ctx);
      });
      centrifuge.connect();
      channels.forEach((channelName) => {
        const channel = centrifuge.newSubscription(channelName);
        channel.on('publication', (message) => {
          this.handleNotification(message.data);
        });
        channel.subscribe();
      });
    },
    handleNotification(message) {
      switch (message.type) {
        case 'PROGRAM_SERVICES_GENERATION_COMPLETED':
          this.$notify.success({
            dangerouslyUseHTMLString: true,
            message: `${this.$t('adminTitles.ServicesHaveGenerated', {programId: message.programServicesGenerationCompletedDetails.programId})}`,
            duration: 15000,
          });
          this.$store.dispatch(PROGRAMS_LIST_REQUEST);
          if (this.$route.params.id) {
            this.$store.dispatch(PROGRAM_DETAILS_REQUEST, {
              packageId: this.$route.params.id,
            });
          }
          break;
        case 'INSURANCE_CONVERSATION_MESSAGE_SEND_BULK_COMPLETED':
          this.$notify.success({
            message: `${this.$t('adminTitles.MessagesSent', {
              count: `${message.insuranceConversationMessageSendBulkCompletedDetails?.sendSuccessCount}`,
            })}`,
          });
          this.$store.dispatch(PROGRAMS_LIST_REQUEST);
          break;
        case 'TASK_ASSIGNED':
          this.$notify.warning({
            dangerouslyUseHTMLString: true,
            message: `${this.$t(`notifications.messages.${message.type}`, {
              name: `${message.taskAssigned?.authorName}`,
              link: `/admin/tasks?taskId=${message.taskAssigned.taskId}`
            })}`,
          });
          this.$store.dispatch('fetchTasks');
          break;
        case 'TASK_EXPIRED':
          this.$notify.warning({
            dangerouslyUseHTMLString: true,
            message: `${this.$t(`notifications.messages.${message.type}`, {
              link: `/admin/tasks?taskId=${message.taskExpired.taskId}`
            })}`,
          });
          this.$store.dispatch('fetchTasks');
          break;
        case 'LEAD_CREATED':
          this.$notify.warning({
            dangerouslyUseHTMLString: true,
            message: `${this.$t(`notifications.messages.${message.type}`, {
              link: `/admin/leads/${message.leadCreated?.leadId}`,
            })}`,
          });
          break;
        case 'LEAD_MESSAGE_CREATED':
          if (!this.userSettings.settings.notification.leadMessageCreated.browser) {
            return;
          }
          this.$notify.warning({
            dangerouslyUseHTMLString: true,
            message: `${this.$t(`notifications.messages.${message.type}`, {
              link: `/admin/leads/${message.leadMessageCreated?.leadId}`,
              fullName: message.leadMessageCreated.fullName,
              message: this.truncate(message.leadMessageCreated.message, 100),
            })}`,
          });
          break;
        case 'TICKET_CREATED':
          this.$notify.warning({
            dangerouslyUseHTMLString: true,
            message: `${this.$t(`notifications.messages.${message.type}`, {
              link: `/admin/tickets/${message.ticketCreated?.ticketId}`,
            })}`,
          });
          break;
        case 'TICKET_MESSAGE_CREATED':
          if (!this.userSettings.settings.notification.ticketMessageCreated.browser) {
            return;
          }
          this.$notify.warning({
            dangerouslyUseHTMLString: true,
            message: `${this.$t(`notifications.messages.${message.type}`, {
              link: `/admin/tickets/${message.ticketMessageCreated?.ticketId}`,
              fullName: message.ticketMessageCreated.fullName,
              message: this.truncate(message.ticketMessageCreated.message, 100),
            })}`,
          });
          break;
        case 'TICKET_LIST_UPDATED':
          this.$store.dispatch(TICKET_LIST_REQUEST);
          break;
        // Clara handed a client over to this user (or to the admins, for an alert).
        case 'TICKET_HANDED_OVER':
          if (this.$store.getters.claraFollowUpsEnabled) {
            this.$notify.warning({
              dangerouslyUseHTMLString: true,
              message: `${this.$t(`notifications.messages.${message.type}`, {
                link: `/admin/tickets/${message.ticketCreated?.ticketId}`,
              })}`,
            });
          }
          this.$store.dispatch(TICKET_LIST_REQUEST);
          this.debouncedFetchBadges();
          break;
        case 'NOTIFICATION_BADGE_UPDATED':
          this.debouncedFetchBadges();
          break;
        // The header banner tracks WhatsApp connection state, so both directions matter: one to
        // raise the strip, the other to clear it once the number is back.
        case 'WHATSAPP_DISCONNECTED':
        case 'WHATSAPP_CONNECTED':
          this.debouncedFetchPlatformAlerts();
          break;
        default:
          break;
      }
    },
  },
};
</script>
